import React, { useState, useRef, useEffect } from "react";
import {
  User,
  LogOut,
  Settings,
  Mail,
  BookOpen,
  FileText,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import TimeoutSnackbar from "../common/TimeoutSnackbar";

const ProfileMenu = () => {
  const [open, setOpen] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [countdown, setCountdown] = useState(3);

  const ref = useRef(null);
  const navigate = useNavigate();
  const user = useSelector((state) => state.user.profile);

  /* AUTH CHECK (DO NOT NAVIGATE IMMEDIATELY) */
  useEffect(() => {
    const token = sessionStorage.getItem("authToken");
    if (!token) {
      setRedirecting(true);
    }
  }, []);

  /* COUNTDOWN + REDIRECT */
  useEffect(() => {
    if (!redirecting) return;

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev === 1) {
          sessionStorage.clear();
          navigate("/login", { replace: true });
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [redirecting, navigate]);

  /* CLOSE ON OUTSIDE CLICK */
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () =>
      document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  /* LOGOUT */
  const handleLogout = () => {
    sessionStorage.clear();
    setOpen(false);
    navigate("/login", { replace: true });
  };

  /* NAVIGATION */
  const goTo = (path) => {
    setOpen(false);
    navigate(path);
  };

  return (
    <>
      {/* 🔔 Timeout Snackbar */}
      {redirecting && <TimeoutSnackbar countdown={countdown} />}

      <div className="relative" ref={ref}>
        {/* Profile icon */}
        <button
          onClick={() => setOpen((o) => !o)}
          className="p-2 rounded-lg hover:bg-[var(--color-primary)]"
        >
          <User size={20} style={{ color: 'var(--color-text-primary)' }} />
        </button>

        {/* Dropdown */}
        {open && (
          <div
            className="absolute right-0 mt-2 w-64 rounded-lg z-50"
            style={{
              backgroundColor: 'var(--color-surface)',
              boxShadow: 'var(--shadow-xl)',
              border: '1px solid var(--color-border-primary)',
              borderRadius: 'var(--radius-lg)',
            }}
          >
            {/* Header */}
            <div
              className="flex items-center gap-3 border-b"
              style={{
                padding: 'var(--spacing-md)',
                borderBottomColor: 'var(--color-border-primary)',
              }}
            >
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center"
                style={{
                  backgroundColor: 'var(--color-surface-hover)',
                  borderRadius: 'var(--radius-full)',
                }}
              >
                <User size={20} style={{ color: 'var(--color-text-secondary)' }} />
              </div>
              <div>
                <div
                  style={{
                    fontWeight: 'var(--font-weight-semibold)',
                    fontSize: 'var(--font-size-sm)',
                    color: 'var(--color-text-primary)',
                  }}
                >
                  {user?.full_name || "No username"}
                </div>
                <div
                  style={{
                    fontSize: 'var(--font-size-sm)',
                    color: 'var(--color-text-secondary)',
                  }}
                >
                  {user?.email || "No email"}
                </div>
              </div>
            </div>

            {/* Menu */}
            <div style={{ padding: 'var(--spacing-sm) 0' }}>
              <MenuItem icon={<Mail size={16} />} label="Inbox" />
              <MenuItem
                icon={<Settings size={16} />}
                label="Account Settings"
                onClick={() => goTo("/settings")}
              />
              <MenuItem
                icon={<Mail size={16} />}
                label="Alerts"
                onClick={() => goTo("/settings/alerts")}
              />
              <MenuItem icon={<BookOpen size={16} />} label="Saved Articles" />
              <MenuItem icon={<FileText size={16} />} label="My Notes" />

              <div
                style={{
                  borderTop: '1px solid var(--color-border-secondary)',
                  margin: 'var(--spacing-sm) 0',
                }}
              />

              <MenuItem
                icon={<LogOut size={16} />}
                label="Sign out"
                danger
                onClick={handleLogout}
              />
            </div>
          </div>
        )}
      </div>
    </>
  );
};

const MenuItem = ({ icon, label, danger, onClick }) => {
  const [isHovered, setIsHovered] = React.useState(false);

  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className="w-full flex items-center text-left"
      style={{
        padding: 'var(--spacing-sm) var(--spacing-md)',
        fontSize: 'var(--font-size-sm)',
        gap: 'var(--spacing-sm)',
        backgroundColor: isHovered ? 'var(--color-surface-hover)' : 'transparent',
        color: danger ? 'var(--color-error)' : 'var(--color-text-primary)',
        border: 'none',
        cursor: 'pointer',
        transition: 'var(--transition-fast)',
      }}
    >
      {icon}
      {label}
    </button>
  );
};

export default ProfileMenu;
